import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Checkbox, CheckboxChangeEvent, Grid, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomMessage, NoData, Title } from "../../components";
import { P1 } from "../../components/NormalFonts";
import { useLayout } from "../../context/LayoutContext";
import { queryKey } from "../../enumerations/queryKey";
import {
  useDeleteFromCart,
  useGetCartItems,
} from "../../hooks/Cart/cart.hooks";
import { DeleteCartParams } from "../../hooks/Cart/data";
import { SearchResponse } from "../../hooks/Search/data";
import { ContentWrapper } from "../../layouts/components";
import { moneyFormat } from "../../plugins/numberFormat";
import { pushGtag } from "../../plugins/utils";
import { SkeletonCollapse } from "../Faq/components";
import { steps } from "../Result/enumerations/steps";
import { CartItem } from "./components";
import styles from "./styleModule";

const Cart: React.FC = () => {
  const { useBreakpoint } = Grid;
  const { getHeaderHeight } = useLayout();
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { confirm } = Modal;
  const { data: cartItems, isLoading: loadingCart } = useGetCartItems({});
  const { mutateAsync: deleteItemFromCart } = useDeleteFromCart();
  const [orderSelector, setOrderSelector] = useState<SearchResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const [isSameBranch, setIsSameBranch] = useState<boolean>(true);
  const navigate = useNavigate();

  const getTotalAmount = () => {
    return orderSelector
      .filter((order: SearchResponse) => order.checked === true)
      .reduce((acc: number, val: SearchResponse) => {
        return acc + val.total_price;
      }, 0);
  };

  useEffect(() => {
    if (!cartItems) return;
    setOrderSelector(cartItems.order_items);
    const hasSoldOutItems = cartItems.order_items.some(
      (item: SearchResponse) => item.cart_status === false
    );
    if (hasSoldOutItems) {
      confirm({
        title: t("cart.soldout.title"),
        content: t("cart.soldout.content"),
        icon: null,
        okText: t("normal.understand"),
        cancelButtonProps: { style: { display: "none" } },
        onOk: () => {},
        onCancel: () => {},
      });
    }
  }, [cartItems]);

  useEffect(() => {
    const activeOrder = orderSelector.filter(
      (order: SearchResponse) => order.cart_status === true
    );
    const selectedOrder = activeOrder.filter(
      (order: SearchResponse) => order.checked === true
    );
    console.debug("activeOrder", activeOrder);
    console.debug("selectedOrder", selectedOrder);

    setIsSelectedAll(
      activeOrder.length === selectedOrder.length && activeOrder.length > 0
    );
  }, [orderSelector]);

  const checkSameBranch = (selector: SearchResponse[], checked: boolean) => {
    // 检查所有选中项目的 branch_id 是否一致
    const activeOrder = selector.filter(
      (order: SearchResponse) => order.cart_status === true
    );
    const selectedOrder = activeOrder.filter(
      (order: SearchResponse) => order.checked === true
    );
    if (selectedOrder.length > 0) {
      const firstBranchId = selectedOrder[0].branch_id;
      const allSameBranch = selectedOrder.every(
        (order: SearchResponse) => order.branch_id === firstBranchId
      );

      if (!allSameBranch) {
        setIsSameBranch(false);
        if (!checked) return;

        confirm({
          title: t("cart.sameModal.title"),
          content: t("cart.sameModal.content"),
          icon: null,
          okButtonProps: { style: { display: "none" } },
          cancelButtonProps: { danger: true, type: "primary" },
          cancelText: t("normal.confirm"),
        });
      } else {
        setIsSameBranch(true);
      }
    }
  };

  useEffect(() => {
    // console.debug("issleect", isSelectedAll);
  }, [isSelectedAll]);

  const handleCheckBoxChange = (
    e: CheckboxChangeEvent,
    index: number,
    item: SearchResponse
  ) => {
    const newOrderSelector = [
      ...orderSelector.slice(0, index),
      { ...orderSelector[index], checked: e.target.checked },
      ...orderSelector.slice(index + 1),
    ];
    setOrderSelector(newOrderSelector);

    checkSameBranch(newOrderSelector, e.target.checked);
  };
  const handleDeleteCart = (item: SearchResponse) => {
    // deleteItemFromCart
    confirm({
      title: t("cart.delete.confirm.title"),
      content: t("cart.delete.confirm.content"),
      icon: null,
      okButtonProps: { danger: true },
      okText: t("normal.delete"),
      cancelText: t("normal.cancel"),
      onOk: async () => {
        setLoading(true);

        const request: DeleteCartParams = {
          cart_number: item.cart_number,
        };
        return await deleteItemFromCart(
          { params: request },
          {
            onSuccess: async (data: any) => {
              setLoading(false);
              queryClient.invalidateQueries({ queryKey: [queryKey.CART] });
            },
            onError: (error: any) => {
              CustomMessage({
                alertMessage: t([
                  `error.${error.data.result.code}`,
                  error.data.result.message,
                ]),
                alertType: "error",
              });
              setLoading(false);
            },
          }
        );
      },
      onCancel() {},
    });
  };

  const renderCartItems = () => {
    if (loadingCart) return <SkeletonCollapse itemsCount={5} />;
    if (!cartItems) return;
    if (orderSelector.length <= 0)
      return (
        <NoData
          title={t("cart.nodata.title")}
          subtitle={t("cart.nodata.subtitle")}
          offsetTop={80}
        />
      );
    return orderSelector.map((item: SearchResponse, index: number) => {
      return (
        <CartItem
          key={`cart_${index}`}
          item={item}
          index={index}
          handleCheckBoxChange={handleCheckBoxChange}
          handleDeleteCart={handleDeleteCart}
        />
      );
    });
  };

  const handleCheckAll = (e: CheckboxChangeEvent) => {
    const updated = orderSelector.map((order: SearchResponse) => {
      if (!order.cart_status) return order;
      return {
        ...order,
        checked: e.target.checked,
      };
    });
    setOrderSelector(updated);
    if (e.target.checked) {
      checkSameBranch(updated, e.target.checked);
    }
  };

  const handleGoToCheckout = () => {
    pushGtag({
      event: "go_to_checkout",
    });

    const checkedOrders = orderSelector.filter(
      (order: SearchResponse) => order.checked === true
    );
    navigate(`/search/?step=${steps.PAY}`, {
      state: { cartItems: checkedOrders },
    });
  };

  return (
    <ContentWrapper>
      <div
        style={{ "--marginTop": getHeaderHeight() } as React.CSSProperties}
        className={styles.cartContainer}
      >
        <Title title={t("cart.title")} subtitle={t("cart.subtitle")} />
        <div
          className={`${styles.itemWrapper} ${screens.sm ? "" : styles.mobile}`}
        >
          {renderCartItems()}
        </div>
        {orderSelector.length > 0 ? (
          <Card
            variant={"borderless"}
            className={styles.summaryCard}
            styles={{ body: screens.sm ? { padding: 24 } : { padding: 12 } }}
          >
            <div
              className={`${styles.summaryWrapper} ${
                screens.sm ? "" : styles.mobile
              }`}
            >
              <Checkbox onChange={handleCheckAll} checked={isSelectedAll}>
                {t("cart.checkall", {
                  count: cartItems?.order_items.filter(
                    (item) => item.cart_status
                  ).length,
                })}
              </Checkbox>
              <div className={styles.rightContainer}>
                <P1
                  className={styles.total}
                  weight="heavy"
                  color="var(--primary)"
                >{`${t(
                  `currency.${process.env.REACT_APP_CURRENCY}`
                )}${moneyFormat(getTotalAmount())}`}</P1>
                <Button
                  onClick={handleGoToCheckout}
                  block={!screens.sm}
                  loading={loading}
                  type={"primary"}
                  disabled={
                    !orderSelector.some(
                      (order: SearchResponse) => order.checked === true
                    ) || !isSameBranch
                  }
                >
                  {t("cart.gotocheckout")}
                </Button>
              </div>
            </div>
          </Card>
        ) : null}
      </div>
    </ContentWrapper>
  );
};

export default Cart;
